import {
  alpha,
  Alert,
  Box,
  Button as MUIButton,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  Stack,
  styled,
  Theme,
} from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { usePermissions, useTranslate, useNotify, useDelete, Button } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../components/custom-breadcrumbs';
import { Iconify } from '../../../../components/iconify';
import { DashboardContent } from '../../../../layouts/dashboard';
import { paths } from 'src/routes/paths';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import IndeterminateCheckBoxRoundedIcon from '@mui/icons-material/IndeterminateCheckBoxRounded';
import DisabledByDefaultRoundedIcon from '@mui/icons-material/DisabledByDefaultRounded';
import useRoles from './useRole';
import { Role } from '../../entities/role.entity';
import { Permission, PermissionData } from '../../entities/permission.entity';
import { hasPermission } from '../../utils/has-permission';
import CreatePopup from './CreatePopup';
import EditPopupV2 from './EditPopupV2';
import { Resources } from '../resource';
import AuditLogDialog from '../../components/AuditLog/AuditLogDialog';
import PermissionPicker from '../../components/PermissionPicker';
import { flattenPermissionGroups } from '../../utils/permission-menu-groups';

const transformRolesToTreeItems = (roles: Role[]): any[] =>
  roles.map((role) => ({
    id: role.id,
    label: role.name,
    children: role.childrens && role.childrens.length > 0 ? transformRolesToTreeItems(role.childrens) : undefined,
  }));

const renderTreeItems = (items: any[]): JSX.Element[] =>
  items.map((item) => (
    <CustomTreeItem
      key={item.id}
      itemId={item.id}
      label={item.label}
      sx={{
        flexGrow: 1,
        '& .MuiTreeItem-content': {
          height: 40,
          px: 1.5,
          borderRadius: 1,
          '&:hover': { backgroundColor: 'action.hover' },
        },
      }}
    >
      {item.children && renderTreeItems(item.children)}
    </CustomTreeItem>
  ));

const getAllIds = (items: any[]): string[] =>
  items.flatMap((item) => [String(item.id), ...(item.children ? getAllIds(item.children) : [])]);

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const translate = useTranslate();
  const notify = useNotify();
  const { roles, listPermissions, getRole, assignPermissionsToRole, getListRoles, getListPermissions } = useRoles();
  const { data } = usePermissions<PermissionData>();
  const { permissions = [] } = data || {};
  const [hasAssignPermission, setHasAssignPermission] = useState(false);
  const [hasCreateRole, setHasCreateRole] = useState(false);
  const [hasUpdateRole, setHasUpdateRole] = useState(false);
  const [hasDeleteRole, setHasDeleteRole] = useState(false);
  const [role, setRole] = useState<Role>();
  const [selectedId, setSelectedId] = useState<string>('root');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  // Ids ticked from the last GET /roles/:id; locked on the super-admin role (XBIT-4778, XBIT-4823).
  const [heldPermissionIds, setHeldPermissionIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteOne] = useDelete();
  const isAdminRole = selectedId !== 'root' && role?.isSuperAdmin === true;
  const expandedIds = ['root', ...getAllIds(transformRolesToTreeItems(roles))];
  const flatPermissions = useMemo(() => flattenPermissionGroups(listPermissions), [listPermissions]);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  

  useEffect(() => {
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setHasAssignPermission(hasPermission(permissions, 'roles', 'assign-permission'));
    setHasCreateRole(hasPermission(permissions, 'roles', 'create'));
    setHasUpdateRole(hasPermission(permissions, 'roles', 'update'));
    setHasDeleteRole(hasPermission(permissions, 'roles', 'delete'));
  }, [permissions]);

  useEffect(() => {
    const fetchRole = async () => {
      if (selectedId === 'root') {
        setSelectedPermissionIds([]);
        setRolePermissions([]);
      } else {
        setLoading(true);
        try {
          const rp = await getRole(selectedId);
          setRole(rp);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchRole();
  }, [selectedId, getRole]);

  useEffect(() => {
    if (role && role.permissions && listPermissions) {
      const matched: string[] = [];
      listPermissions.forEach((group: any) => {
        group.resources.forEach((resource: any) => {
          resource.actions.forEach((per: any) => {
            const has = role.permissions?.some(
              (rp) => rp.resource === per.resource && rp.action === per.action && rp.role === role.name
            );
            if (has && per.id) matched.push(per.id);
          });
        });
      });
      setRolePermissions(role.permissions);
      setSelectedPermissionIds(matched);
      setHeldPermissionIds(matched);
    }
  }, [role, listPermissions]);

  const handleRefresh = () => {
    getListRoles();
    getListPermissions();
  };

  const assignPermissions = async () => {
    if (selectedId === 'root') return;
    try {
      await assignPermissionsToRole(selectedId, selectedPermissionIds);
      notify(translate('module.roles.assignPermissionSuccess'), { type: 'success' });
    } catch (error: any) {
      notify(error?.response?.data?.message || error.message, { type: 'error' });
    } finally {
      // Re-read the role so the ticked and locked boxes match what the server holds (XBIT-4778).
      try {
        setRole(await getRole(selectedId));
      } catch {
        // Keep the current screen; the next selection re-reads the role.
      }
    }
  };

  const handleDelete = () => {
    deleteOne(
      Resources.roles,
      { id: selectedId },
      {
        onSuccess: () => {
          notify(translate('module.roles.deleteSuccess'), { type: 'success' });
          setPopup(undefined);
          handleRefresh();
          setSelectedId('root');
        },
        onError: (error: any) => {
          notify(error?.body?.message || error?.message, { type: 'error' });
        },
      }
    );
  };

  return (
    <PermissionGuard resource="roles" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.roles')}
          links={[
            { name: translate('resource.dashboard'), href: paths.dashboard.root },
            { name: translate('resource.roles') },
          ]}
          sx={{ mb: { xs: 3, md: 5 } }}
          action={
            <Stack
            mb={1}
          >
            <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
              <Button
                variant="contained"
                size="medium"
                color="secondary"
                onClick={() => {
                  setSelectedRecord({ alias: 'Roles' });
                  setAuditTitle(`${translate('resource.auditLogs')} - ${translate('module.roles.title')}`);
                  setAuditLogOpen(true);
                }}
                label={translate('resource.auditLogs')}
              >
                <Iconify icon="ph:note" />
              </Button>
            </PermissionGuard>
          </Stack>
          }
        />

        <Stack direction="row" flexWrap="wrap" spacing={2} sx={{ mb: 1 }}>
          <Card elevation={3} sx={{ borderRadius: 2, width: { xs: '100%', md: '48%' } }}>
            <CardHeader
              title={translate('resource.roles')}
              action={
                <Stack direction="row" spacing={1}>
                  {hasCreateRole && (
                    <MUIButton
                      variant="contained"
                      size="small"
                      startIcon={<Iconify icon="eva:plus-fill" />}
                      onClick={() =>
                        setPopup(
                          <CreatePopup
                            onClose={() => setPopup(undefined)}
                            selectedId={selectedId}
                            onRefresh={handleRefresh}
                          />
                        )
                      }
                    >
                      {translate('ra.action.create')}
                    </MUIButton>
                  )}
                  {hasUpdateRole && (
                    <MUIButton
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<Iconify icon="eva:edit-fill" />}
                      disabled={selectedId === 'root'}
                      onClick={() =>
                        setPopup(
                          <EditPopupV2
                            id={selectedId}
                            onClose={() => setPopup(undefined)}
                            onRefresh={handleRefresh}
                            roleItem={role}
                          />
                        )
                      }
                    >
                      {translate('ra.action.edit')}
                    </MUIButton>
                  )}
                  {hasDeleteRole && (
                    <MUIButton
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<Iconify icon="eva:trash-fill" />}
                      disabled={selectedId === 'root'}
                      onClick={handleDelete}
                    >
                      {translate('ra.action.delete')}
                    </MUIButton>
                  )}
                </Stack>
              }
              sx={{
                p: 2,
                borderBottom: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.12)}`,
                '& .MuiCardHeader-action': { alignSelf: 'center' },
              }}
            />
            <CardContent sx={{ pt: 1 }}>
              {roles && roles.length > 0 ? (
                <SimpleTreeView
                  defaultExpandedItems={expandedIds}
                  onItemFocus={(_, nodeId) => setSelectedId(nodeId)}
                  slots={{ expandIcon: ExpandIcon, collapseIcon: CollapseIcon, endIcon: EndIcon }}
                  sx={{ overflowX: 'hidden', height: { md: '60vh' } }}
                >
                  {renderTreeItems([
                    { id: 'root', label: 'Root', children: transformRolesToTreeItems(roles) },
                  ])}
                </SimpleTreeView>
              ) : (
                <Box sx={{ overflowX: 'hidden', height: { md: '60vh' }, flexGrow: 1 }} />
              )}
            </CardContent>
          </Card>

          <Card elevation={3} sx={{ borderRadius: 2, width: { xs: '100%', md: '48%' } }}>
            <CardHeader
              title={translate('resource.permissions')}
              action={
                hasAssignPermission && (
                  <MUIButton
                    variant="contained"
                    size="small"
                    startIcon={<Iconify icon="eva:save-fill" />}
                    disabled={selectedId === 'root' || loading}
                    onClick={assignPermissions}
                  >
                    {translate('resource.save')}
                  </MUIButton>
                )
              }
              sx={{
                p: 2,
                borderBottom: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.12)}`,
                '& .MuiCardHeader-action': { alignSelf: 'center' },
              }}
            />
            <CardContent sx={{ pt: 1 }}>
              {/*
                Every checkbox stays disabled until a role is picked on the left,
                which reads as a broken screen unless we say so.
              */}
              {selectedId === 'root' && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {translate('module.permissions.pickRoleFirst')}
                </Alert>
              )}
              {isAdminRole && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {translate('module.permissions.adminRoleLocked', {
                    _: 'Permissions cannot be removed from the admin role. You can still add new ones.',
                  })}
                </Alert>
              )}
              {flatPermissions.length > 0 ? (
                <PermissionPicker
                  permissions={flatPermissions}
                  selectedIds={selectedPermissionIds}
                  onSelectedIdsChange={setSelectedPermissionIds}
                  lockedIds={isAdminRole ? heldPermissionIds : undefined}
                  disabled={!hasAssignPermission || selectedId === 'root' || loading}
                  height="60vh"
                />
              ) : (
                <Box sx={{ overflowX: 'hidden', height: { md: '60vh' }, flexGrow: 1 }} />
              )}
            </CardContent>
          </Card>
        </Stack>

        <Dialog open={!!popup} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
          {popup}
        </Dialog>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          resource={Resources.roles}
          recordId={selectedRecord?.id}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

const CustomTreeItem = styled(TreeItem)(({ theme }: { theme: Theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.2, 0),
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

const ExpandIcon = (props: React.PropsWithoutRef<typeof AddBoxRoundedIcon>) => (
  <AddBoxRoundedIcon {...props} sx={{ opacity: 0.8 }} />
);
const CollapseIcon = (props: React.PropsWithoutRef<typeof IndeterminateCheckBoxRoundedIcon>) => (
  <IndeterminateCheckBoxRoundedIcon {...props} sx={{ opacity: 0.8 }} />
);
const EndIcon = (props: React.PropsWithoutRef<typeof DisabledByDefaultRoundedIcon>) => (
  <Box {...props} sx={{ width: 0 }} />
);

export default View;